import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged, 
  User,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification,
  getIdToken,
  getIdTokenResult
} from 'firebase/auth';
import { doc, setDoc, getDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

export interface UserProfile {
  uid: string;
  email: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  createdAt: string; // ISO string
  lastLoginAt: string; // ISO string
}

export interface AuthError {
  code: string;
  message: string;
}

// Helper to convert Firestore Timestamps and JS Dates to ISO strings
function convertTimestamps(obj: any) {
  const result: any = { ...obj };
  for (const key in result) {
    if (result[key] instanceof Timestamp) {
      result[key] = result[key].toDate().toISOString();
    } else if (result[key] instanceof Date) {
      result[key] = result[key].toISOString();
    }
  }
  return result;
}

// Get current user's ID token
export const getCurrentUserToken = async (): Promise<string | null> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }
    
    const token = await getIdToken(user);
    return token;
  } catch (error: any) {
    console.error('Error getting ID token:', error);
    return null;
  }
};

// Get detailed token information
export const getTokenInfo = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }
    
    const tokenResult = await getIdTokenResult(user);
    return tokenResult;
  } catch (error: any) {
    console.error('Error getting token info:', error);
    return null;
  }
};

// Force refresh the ID token
export const refreshToken = async (): Promise<string | null> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }
    
    // Force refresh the token
    const token = await getIdToken(user, true);
    return token;
  } catch (error: any) {
    console.error('Error refreshing token:', error);
    return null;
  }
};

// Check if token is expired
export const isTokenExpired = async (): Promise<boolean> => {
  try {
    const tokenInfo = await getTokenInfo();
    if (!tokenInfo) {
      return true;
    }
    
    const now = Date.now() / 1000; // Convert to seconds
    const expirationTime = typeof tokenInfo.expirationTime === 'string' 
      ? parseInt(tokenInfo.expirationTime) 
      : tokenInfo.expirationTime;
    return expirationTime < now;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
};

// Sign up with email and password
export const signUp = async (
  email: string,
  password: string
): Promise<UserProfile> => {
  try {
    // Create user with Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Create user profile in Firestore
    const now = new Date().toISOString();
    const userProfile: UserProfile = {
      uid: user.uid,
      email: user.email!,
      createdAt: now,
      lastLoginAt: now,
    };

    await setDoc(doc(db, 'users', user.uid), userProfile);

    // Send email verification
    await sendEmailVerification(user);

    return userProfile;
  } catch (error: any) {
    throw new Error(getAuthErrorMessage(error.code));
  }
};

// Sign in with email and password
export const signIn = async (email: string, password: string): Promise<UserProfile> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Get user profile from Firestore
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    
    if (!userDoc.exists()) {
      throw new Error('User profile not found');
    }

    const userProfile = userDoc.data() as UserProfile;
    const convertedProfile = convertTimestamps(userProfile);
    const now = new Date().toISOString();
    // Update last login time
    await setDoc(doc(db, 'users', user.uid), {
      ...convertedProfile,
      lastLoginAt: now
    }, { merge: true });

    return { ...convertedProfile, lastLoginAt: now };
  } catch (error: any) {
    throw new Error(getAuthErrorMessage(error.code));
  }
};

// Sign out
export const signOutUser = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error: any) {
    throw new Error(getAuthErrorMessage(error.code));
  }
};

// Get current user
export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};

// Listen to auth state changes
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// Reset password
export const resetPassword = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error: any) {
    throw new Error(getAuthErrorMessage(error.code));
  }
};

// Get user profile from Firestore
export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      const data = userDoc.data() as UserProfile;
      return convertTimestamps(data);
    }
    return null;
  } catch (error: any) {
    throw new Error(getAuthErrorMessage(error.code));
  }
};

// Helper function to get user-friendly error messages
const getAuthErrorMessage = (code: string): string => {
  switch (code) {
    case 'auth/user-not-found':
      return 'No account found with this email address.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters long.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection.';
    default:
      return 'An error occurred. Please try again.';
  }
}; 